import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useReveal } from './useReveal';
import ImpactCalculator from './ImpactCalculator';
import AudienceTabs from './AudienceTabs';
import WeTakeStrip from './WeTakeStrip';
import ItemJourney from './ItemJourney';
import ChapterNav from './ChapterNav';
import { track } from './analytics';
import { useLang } from './i18n';
import { BOOKINGS_OPEN } from './siteMode';
import './HomePage.css';

/** All homepage copy, in both languages. `lang` picks the set at render time. */
const HOME_COPY = {
  en: {
    marquee: 'FREE PICKUPS ♻ ELECTRONICS ♻ CLOTHES ♻ FURNITURE ♻ BROOKLYN BORN ♻ SECOND CHANCES ♻ ALL 5 BOROUGHS ♻',
    how: {
      label: 'How It Works', title: 'Giving back is this easy.', sub: 'From your doorstep to a better planet — in three simple steps.',
      steps: [
        { num: 'Step 01', h: 'Schedule', p: 'Pick a date and time that works for you. We’ll send a confirmation and reminder so you never miss it.' },
        { num: 'Step 02', h: 'We Pick Up', p: 'Our friendly team comes right to your door — for electronics, clothes, textiles, and bulk junk removal.' },
        { num: 'Step 03', h: 'See Your Impact', p: 'Items are refurbished, donated, or responsibly recycled. Watch the community impact add up.' },
      ],
    },
    services: {
      label: 'Our Services', title: 'What we pick up.', sub: 'All pickups are 100% free — for homes, businesses, schools, and shelters.',
      cards: [
        { h: 'Electronics Recycling', p: 'Laptops, phones, tablets, e-waste — responsibly recycled or refurbished for those in need.' },
        { h: 'Textile Recycling', p: 'Clothing, shoes, linens, and more. We clean out closets and keep textiles out of landfills.' },
        { h: 'Junk Removal', p: 'Bulk pickups for businesses, schools, and homes. We handle the heavy lifting and sort for donation or recycling.' },
      ],
    },
    biz: {
      label: 'For Businesses & Offices', title: 'Clearing out the office? We make it effortless.',
      sub: 'Upgrading, moving, or downsizing? Everything Eco handles bulk e-waste, electronics, furniture and more — responsibly recycled or donated, with the paperwork your business needs.',
      badge: 'Businesses love this',
      cards: [
        { h: 'Secure Data Destruction', p: 'We wipe or physically destroy hard drives and data-bearing devices — so your company’s data never leaves with your old tech.' },
        { h: 'Bulk E-Waste & Electronics', p: 'Old computers, monitors, phones, and cables — collected in bulk and responsibly recycled or refurbished for people who need them.' },
        { h: 'Office Cleanouts & Moves', p: 'Renovating or relocating? We clear furniture, electronics, and supplies fast — and keep it all out of the landfill.' },
        { h: 'Impact Reports (ESG / CSR)', p: 'Get a clear report of the waste diverted and community impact of your cleanout — perfect for your sustainability story.' },
        { h: 'Donation Receipts', p: 'Through our nonprofit partners, qualifying donations can come with tax-deductible receipts for your business.' },
      ],
      trust: ['🔒 Secure data handling', '✅ Free quotes', '📅 Flexible scheduling', '🗽 All 5 boroughs', '♻️ Responsible recycling'],
      cta: 'Get a Free Business Quote →',
    },
    impact: {
      label: 'Community Impact', title: 'Real numbers. Real change.', sub: 'Every pickup adds up. Here’s what the Everything Eco community has accomplished together.',
      stats: ['Trees Saved', 'lbs CO₂ Saved', 'lbs Landfill Cleared'],
    },
    why: {
      label: 'Why Everything Eco', title: 'The greener, friendlier choice.',
      sub: 'Most recycling and disposal companies make you call for a quote, charge fees, and only serve big businesses. We do it differently.',
      us: 'Everything Eco', them: 'Typical company',
      rows: [
        ['100% free home pickups', 'Often paid, with hidden fees'],
        ['Upfront & transparent', '“Call us for a quote”'],
        ['Secure data destruction', 'Enterprise clients only'],
        ['Serves homes AND businesses', 'Businesses only'],
        ['Local, community-driven', 'Faceless corporate vendor'],
        ['Real impact: youth jobs & gardens', 'A compliance checkbox'],
        ['Earn rewards for recycling', 'No rewards'],
      ],
    },
    rewards: {
      label: 'Rewards Program', title: 'Do good. Earn big.', sub: 'Every pickup earns you EcoPoints. Redeem them for real rewards — from gift cards to planting trees.',
      popular: 'Popular',
      cards: [
        { pts: '250 EcoPoints', name: '$5 Coffee Gift Card', desc: 'Treat yourself at local partner cafés across NYC.' },
        { pts: '500 EcoPoints', name: 'Everything Eco Tote', desc: 'Our signature reusable tote — made from 100% recycled materials.' },
        { pts: '750 EcoPoints', name: 'Plant a Tree', desc: 'We’ll plant a real tree in your name through our partners.' },
        { pts: '1,000 EcoPoints', name: '$20 Store Credit', desc: 'Use toward any sustainable brand in our partner marketplace.' },
      ],
      ctaP: 'Earn 100 EcoPoints for every pickup. Your first pickup is double points!',
      ctaBtn: 'Start Earning Today 🎉',
    },
    about: {
      label: 'Our Mission', title1: 'Built for NYC.', title2: 'Built for the planet.',
      tiles: [['2021', 'Founded in Brooklyn'], ['NYC', 'All 5 Boroughs'], ['Free', 'Always, for everyone'], ['Community First', 'Turning trash into opportunity']],
      body: 'Everything Eco was created to give underserved communities access to recycling, sustainability, and opportunity. We started this mission in Williamsburg, Brooklyn, where trash and e-waste pile up in underserved neighborhoods while wealthier areas stay clean. Our mission is to turn trash into opportunity by collecting, repairing, and redistributing electronics, clothing, and other reusable items to keep them out of landfills and in the hands of those who need them. This isn’t just about recycling — it’s about community empowerment. Every pickup, every refurbished laptop, and every donated shirt is one step closer to making sustainability cool, accessible, and life-changing for people who feel left out of the system. We’re building this for families, small businesses, schools, shelters, and anyone who wants to help NYC become cleaner and greener.',
    },
    testimonials: {
      title: 'What our community says',
      items: [
        { name: 'Maria G.', text: 'Everything Eco picked up my old laptop and clothes. I love knowing they went to someone who needs them! Super easy and friendly.' },
        { name: 'Brooklyn Diner', text: 'We had a bulk pickup for our restaurant. Fast, professional, and we got a tax receipt for our donation. Highly recommend!' },
        { name: 'Jamal S.', text: 'The team explained how my e-waste would be recycled. I feel good about helping the community and the planet.' },
      ],
    },
    founder: {
      label: 'Meet the Founders', title: 'Hi, I’m Julio 👋', badge: 'Julio · Founder & CEO',
      quote: '“Nothing with value should ever be wasted. Not electronics. Not neighborhoods. Not opportunities. And certainly not people.”',
      p1: 'I was born in Brooklyn Hospital and raised in Williamsburg — my family’s been here for decades. Everything Eco was born from my life.',
      p2: 'My father found value where others saw waste. He’d take furniture and electronics people threw away, repair them, and ship them to families in the Dominican Republic who couldn’t otherwise afford them. As a kid I didn’t always get it. Now I know — he wasn’t collecting garbage. He was seeing value other people couldn’t.',
      p3a: 'My mother taught me to create my own opportunities instead of waiting for them. And both of them taught me the lesson underneath everything: ',
      p3strong: 'treat people with dignity.',
      p3b: ' When neighbors came by to sell what little they had, my parents bought what they could and treated them like human beings.',
      p4: 'In 2018 I lost my mother. It taught me that time matters — that I wanted to build something meaningful while I have the chance. Everything Eco is my answer. 💚',
      pro: 'And this isn’t just heart — it’s trade. A workforce program at St. Nicks Alliance led me into Brooklyn’s electronics recycling world, where I tested and refurbished thousands of devices, coordinated pickups across the city, managed inventory, and trained new team members. I kept building with IT training at Per Scholas, and I run an electronics resale business today. Everything Eco stands on that experience.',
      line: 'Recycling is our foundation. People are our purpose.',
      sign: '— Julio Ramirez, Founder of Everything Eco',
      coTitle: 'And meet Nick 🤝',
      coName: 'Nickel “Nick” John', coRole: 'Co-Founder',
      coQuote: '“We want to recycle materials while also recycling knowledge, skills, and opportunity.”',
      coP1: 'Nick was born and raised in Guyana, watching his parents work incredibly hard for the opportunity to bring him to the United States. When he arrived, he saw that struggle everywhere — immigrants and people born here alike, doing everything they could to move forward and support the people they love.',
      coP2: 'He’s been taking electronics apart since he was a kid. Between working with iFixit, helping manage electronics recovery operations in New York City, and software-engineering training through the Asurion Academy, he became my mentor in this industry — the person who taught me how professional testing, repair, and recycling systems really work.',
      coP3: 'Throughout his life, Nick met friends, relatives, and coworkers who were ready to work, learn, and change their lives — but were rarely given a real chance to prove themselves. That’s one of the main reasons he helped start Everything Eco: to give people skills, knowledge, and opportunities they can carry for the rest of their lives.',
      coMore: 'Read Nick’s story, in his own words ↓',
      coLess: 'Close Nick’s story ↑',
      coSign: '— Nick John, Co-Founder of Everything Eco',
      coFull: [
        'I was born and raised in Guyana, South America. Growing up, I watched my parents work incredibly hard for the opportunity to bring me to the United States and build a better life for our family.',
        'When I arrived in this country, I saw that struggle everywhere. I saw immigrants working tirelessly to create a future for their families, but I also saw people who were born here facing many of the same obstacles. They were doing everything they could to survive, move forward, support the people they loved, and contribute to the world around them.',
        'Those experiences taught me how deeply connected we all are.',
        'Throughout my life, I have met friends, relatives, coworkers, and members of my community who were searching for a second chance. Many of them were ready to work, learn, and change their lives, but they were rarely given a real opportunity to prove themselves.',
        'That is one of the main reasons I helped start Everything Eco.',
        'Through this company, I want to provide people with practical skills, knowledge, guidance, and opportunities that they can carry with them for the rest of their lives. Someone may use those skills to grow within Everything Eco, qualify for another career, support their family, or eventually start a company of their own.',
        'I watched my parents work hard for years without always being given a fair opportunity to advance. I saw what it was like to work without knowing whether something better would ever come. I do not want that to be the future for the next generation.',
        'At the same time, our planet is burning through valuable resources. Every day, we throw away electronics, plastic, paper, clothing, equipment, knowledge, talent, and human potential — often before recognizing their true value.',
        'Everything Eco is built on the belief that these things should not be wasted.',
        'We want to recycle materials while also recycling knowledge, skills, and opportunity. By giving resources a second life and giving people a second chance, we can strengthen our communities, create new pathways forward, and protect the planet at the same time.',
        'Because when people are given the tools and opportunity to grow, everyone benefits.',
      ],
      together: 'Two New Yorkers. One belief: nothing — and no one — should be treated like waste.',
    },
    programs: {
      label: 'Community & Programs', title1: 'More than recycling —', title2: 'it’s a movement. 🗽',
      sub: 'Everything Eco is built block by block. We put NYC youth to work, green our neighborhoods, and teach the next generation to care for the city they call home.',
      cards: [
        { h: 'Youth Cleanup Crews', p: 'We hire and train local young people for neighborhood cleanups and pickups — paid work that builds skills, pride, and a cleaner city.' },
        { h: 'Community Gardens', p: 'We help turn vacant lots into thriving green spaces, supporting neighborhood gardens across Brooklyn and beyond.' },
        { h: 'Recycling Education', p: 'Hands-on workshops in schools and community centers — teaching how (and why) to recycle the right way.' },
        { h: 'Neighborhood Drives', p: 'Pop-up e-waste, clothing, and food drives that bring the whole block together for a common cause.' },
      ],
    },
    heroes: {
      kicker: '★ The Everything Eco Universe ★', title: 'Join the Eco-Allies',
      sub: 'Trash-Bot is growing — built from everything our city throws away. Every pickup you schedule is a strike against it. Suit up, New York. 🦸‍♀️🦸',
      trailerTag: '🎬 OFFICIAL TEASER',
      trailerCap: 'Earth Jr. and the Eco-Force — tap for sound 🔊',
      roster: [
        'The hero. Brave, hopeful, Brooklyn-born.',
        'Collects & sorts everything that can be reused.',
        'Smart and cool. Keeps our water clean.',
        'Harnesses the sun to fuel the team.',
        'Fast, breezy, keeps the air moving.',
        'Strong and wise. Protects all living things.',
      ],
      cap1: 'Issue #1 — “Trash-Bot Rises”', cap2: 'Eco-Heroes, on the streets of NYC',
      shopTitle: '🛍️ The Eco-Hero Shop', shopSub1: 'Comics, vinyl figures & collectibles — ', shopSub2: 'dropping soon!',
      soon: 'Coming soon', kidsFree: 'Kids get 1 FREE 🎁', notify: '🔔 Notify me',
      shop: [
        { h: 'Earth Jr. — Planet Hero', p: 'Series 1 collectible vinyl figure with cape & glowing recycle emblem.' },
        { h: 'Eco-Allies Mini-Figures', p: 'Collect all 5! Recycle Bin, Drip, Windy, Sunny & Sprout. Kids get one free on every pickup.' },
        { h: '“Trash-Bot Rises” — Issue #1', p: 'The first Everything Eco comic book. Full-color, collector\'s edition.' },
      ],
      banner1: 'Free mini-figure for kids', banner2: ' on every recycling pickup — gotta collect \'em all!',
      btnPlay: '🎮 Play "Trash-Bot Rises"', btnHero: 'Become an Eco-Hero →',
      btnVerse: 'Enter the EcoVerse', btnShop: '🛍️ Visit the Shop',
    },
    doors: {
      title: 'What brings you here today?',
      cards: [
        { h: 'My Home', p: 'Free pickups for electronics, clothing & furniture — right from your door.', go: 'Schedule a pickup' },
        { h: 'My Business', p: 'Cleanouts, e-waste recovery & documentation for offices, buildings & schools.', go: 'Get a free quote' },
        { h: 'Kids & Schools', p: 'Earth Jr., the Eco-Force, games, comics and the Eco Academy.', go: 'Enter the EcoVerse' },
        { h: 'I Need a Hand', p: 'Jobs, training, computers, food & real NYC resources — no shame, just next steps.', go: 'Visit the Opportunity Hub' },
      ],
    },
    partners: {
      label: 'Partner With Us', title: 'Let’s build a greener NYC together.',
      sub: 'We team up with businesses, schools, shelters, and nonprofits across the city to keep good stuff out of landfills and in the hands of people who need it.',
      cards: [
        { h: 'Businesses & Restaurants', p: 'Schedule bulk pickups for old equipment, electronics, and surplus — and get a tax-deductible receipt.' },
        { h: 'Schools & Universities', p: 'Host an e-waste or clothing drive. We’ll handle logistics and share the impact with your community.' },
        { h: 'Shelters & Nonprofits', p: 'Receive refurbished electronics, clean clothing, and essentials for the people you serve.' },
        { h: 'Property & Building Managers', p: 'Offer easy, recurring recycling pickups as a perk for your residents and tenants.' },
      ],
      ctaP: 'Interested in partnering with Everything Eco?', ctaBtn: 'Become a Partner →',
    },
    faq: {
      label: 'Questions? Answered.', title: 'Frequently asked questions',
      items: [
        { q: 'Is it really free?', a: 'Yes! Pickups for e-waste, clothing, and household donations are 100% free for New Yorkers. For large business or office cleanouts, we’ll give you a free, no-obligation quote first — no hidden fees, ever.' },
        { q: 'What can you pick up?', a: 'Electronics and e-waste (laptops, phones, TVs, cables), clothing and textiles, furniture, household goods, and food donations. For businesses we also handle bulk pickups and full office cleanouts.' },
        { q: 'What about my data — is it safe?', a: 'Absolutely. For computers and phones, we can wipe or physically destroy the hard drives so your personal or company data is gone for good. Just let us know when you book your pickup.' },
        { q: 'What happens to my stuff after pickup?', a: 'Working items are cleaned, refurbished, and donated to people and families who need them. Everything else is responsibly recycled. The goal is simple: nothing usable goes to a landfill.' },
        { q: 'Where do you serve?', a: 'All five boroughs of New York City — Brooklyn, Manhattan, Queens, the Bronx, and Staten Island.' },
        { q: 'Do I get a receipt for my donation?', a: 'Yes — qualifying donations can come with a tax-deductible receipt through our nonprofit partners. Just ask when you schedule and we’ll take care of the paperwork.' },
      ],
      ctaP: 'Still have a question?', ctaBtn: 'Get in Touch →',
    },
    news: {
      title: 'Stay in the loop — and on the planet. 🌿',
      sub: 'Get monthly impact updates, recycling tips, partner discounts, and early access to new features.',
      name: 'Your name', email: 'Your email address', btn: 'Subscribe — It’s Free 🎉',
      fine: 'No spam. Unsubscribe anytime. We respect your inbox.',
      alert: "You're subscribed! Welcome to the Everything Eco community 💚",
    },
  },
  es: {
    marquee: 'RECOGIDAS GRATIS ♻ ELECTRÓNICOS ♻ ROPA ♻ MUEBLES ♻ NACIDO EN BROOKLYN ♻ SEGUNDAS OPORTUNIDADES ♻ LOS 5 CONDADOS ♻',
    how: {
      label: 'Cómo Funciona', title: 'Ayudar es así de fácil.', sub: 'De tu puerta a un planeta mejor — en tres simples pasos.',
      steps: [
        { num: 'Paso 01', h: 'Agenda', p: 'Elige el día y la hora que te convenga. Te mandamos confirmación y recordatorio para que no se te pase.' },
        { num: 'Paso 02', h: 'Recogemos', p: 'Nuestro equipo llega hasta tu puerta — por electrónicos, ropa, textiles y trastos en volumen.' },
        { num: 'Paso 03', h: 'Mira tu Impacto', p: 'Los artículos se reparan, se donan o se reciclan responsablemente. Mira cómo crece el impacto comunitario.' },
      ],
    },
    services: {
      label: 'Nuestros Servicios', title: 'Qué recogemos.', sub: 'Todas las recogidas son 100% gratis — para hogares, negocios, escuelas y refugios.',
      cards: [
        { h: 'Reciclaje de Electrónicos', p: 'Laptops, teléfonos, tablets, e-waste — reciclados responsablemente o reparados para quien los necesita.' },
        { h: 'Reciclaje de Ropa', p: 'Ropa, zapatos, sábanas y más. Vaciamos closets y mantenemos los textiles fuera del vertedero.' },
        { h: 'Retiro de Trastos', p: 'Recogidas en volumen para negocios, escuelas y hogares. Nosotros cargamos lo pesado y separamos para donar o reciclar.' },
      ],
    },
    biz: {
      label: 'Para Negocios y Oficinas', title: '¿Vaciando la oficina? Te lo hacemos fácil.',
      sub: '¿Renovando, mudándote o reduciendo espacio? Everything Eco maneja e-waste en volumen, electrónicos, muebles y más — reciclado o donado responsablemente, con los papeles que tu negocio necesita.',
      badge: 'A los negocios les encanta',
      cards: [
        { h: 'Destrucción Segura de Datos', p: 'Borramos o destruimos físicamente los discos duros — para que los datos de tu empresa nunca se vayan con tu tecnología vieja.' },
        { h: 'E-Waste y Electrónicos en Volumen', p: 'Computadoras, monitores, teléfonos y cables viejos — recogidos en volumen y reciclados o reparados para gente que los necesita.' },
        { h: 'Vaciado de Oficinas y Mudanzas', p: '¿Renovando o mudándote? Sacamos muebles, electrónicos y suministros rápido — y todo fuera del vertedero.' },
        { h: 'Reportes de Impacto (ESG / CSR)', p: 'Recibe un reporte claro del desperdicio desviado y el impacto comunitario — perfecto para tu historia de sostenibilidad.' },
        { h: 'Recibos de Donación', p: 'A través de nuestros socios sin fines de lucro, las donaciones que califican pueden traer recibos deducibles de impuestos.' },
      ],
      trust: ['🔒 Manejo seguro de datos', '✅ Cotizaciones gratis', '📅 Horarios flexibles', '🗽 Los 5 condados', '♻️ Reciclaje responsable'],
      cta: 'Cotización Gratis para tu Negocio →',
    },
    impact: {
      label: 'Impacto Comunitario', title: 'Números reales. Cambio real.', sub: 'Cada recogida suma. Esto es lo que la comunidad de Everything Eco ha logrado junta.',
      stats: ['Árboles Salvados', 'lbs de CO₂ Ahorradas', 'lbs Fuera del Vertedero'],
    },
    why: {
      label: 'Por Qué Everything Eco', title: 'La opción más verde y más amigable.',
      sub: 'La mayoría de las compañías de reciclaje te hacen llamar para cotizar, cobran tarifas y solo atienden a las grandes empresas. Nosotros lo hacemos diferente.',
      us: 'Everything Eco', them: 'Compañía típica',
      rows: [
        ['Recogidas a domicilio 100% gratis', 'Casi siempre pagas, con cargos ocultos'],
        ['Claros y transparentes', '“Llámenos para cotizar”'],
        ['Destrucción segura de datos', 'Solo para clientes corporativos'],
        ['Servimos hogares Y negocios', 'Solo negocios'],
        ['Local y de la comunidad', 'Corporación sin rostro'],
        ['Impacto real: empleos juveniles y jardines', 'Solo para cumplir requisitos'],
        ['Gana premios por reciclar', 'Sin premios'],
      ],
    },
    rewards: {
      label: 'Programa de Premios', title: 'Haz el bien. Gana en grande.', sub: 'Cada recogida te da EcoPoints. Canjéalos por premios reales — desde tarjetas de regalo hasta plantar árboles.',
      popular: 'Popular',
      cards: [
        { pts: '250 EcoPoints', name: 'Tarjeta de Café de $5', desc: 'Date un gusto en los cafés locales aliados por todo NYC.' },
        { pts: '500 EcoPoints', name: 'Tote de Everything Eco', desc: 'Nuestra bolsa reusable insignia — hecha 100% de materiales reciclados.' },
        { pts: '750 EcoPoints', name: 'Planta un Árbol', desc: 'Plantamos un árbol de verdad a tu nombre a través de nuestros aliados.' },
        { pts: '1,000 EcoPoints', name: '$20 de Crédito', desc: 'Úsalo en cualquier marca sostenible de nuestro mercado de aliados.' },
      ],
      ctaP: 'Gana 100 EcoPoints por cada recogida. ¡Tu primera recogida vale doble!',
      ctaBtn: 'Empieza a Ganar Hoy 🎉',
    },
    about: {
      label: 'Nuestra Misión', title1: 'Hecho para NYC.', title2: 'Hecho para el planeta.',
      tiles: [['2021', 'Fundado en Brooklyn'], ['NYC', 'Los 5 Condados'], ['Gratis', 'Siempre, para todos'], ['Comunidad Primero', 'Convirtiendo basura en oportunidad']],
      body: 'Everything Eco nació para darle a las comunidades olvidadas acceso al reciclaje, la sostenibilidad y la oportunidad. Empezamos esta misión en Williamsburg, Brooklyn, donde la basura y el e-waste se acumulan en los barrios trabajadores mientras las zonas ricas se mantienen limpias. Nuestra misión es convertir la basura en oportunidad: recolectamos, reparamos y redistribuimos electrónicos, ropa y otros artículos reusables para mantenerlos fuera de los vertederos y en manos de quien los necesita. Esto no es solo reciclaje — es empoderamiento comunitario. Cada recogida, cada laptop reparada y cada camisa donada nos acerca a hacer la sostenibilidad algo cool, accesible y transformador para la gente que el sistema deja fuera. Esto lo construimos para familias, pequeños negocios, escuelas, refugios y cualquiera que quiera ayudar a que NYC sea más limpia y más verde.',
    },
    testimonials: {
      title: 'Lo que dice nuestra comunidad',
      items: [
        { name: 'Maria G.', text: 'Everything Eco recogió mi laptop vieja y mi ropa. ¡Me encanta saber que llegaron a alguien que las necesita! Súper fácil y amables.' },
        { name: 'Brooklyn Diner', text: 'Hicimos una recogida grande para nuestro restaurante. Rápidos, profesionales, y nos dieron recibo de impuestos por la donación. ¡Muy recomendados!' },
        { name: 'Jamal S.', text: 'El equipo me explicó cómo iban a reciclar mi e-waste. Me siento bien ayudando a la comunidad y al planeta.' },
      ],
    },
    founder: {
      label: 'Conoce a los Fundadores', title: 'Hola, soy Julio 👋', badge: 'Julio · Fundador & CEO',
      quote: '“Nada que tenga valor debería desperdiciarse jamás. Ni los electrónicos. Ni los barrios. Ni las oportunidades. Y mucho menos las personas.”',
      p1: 'Nací en el Brooklyn Hospital y me crié en Williamsburg — mi familia lleva décadas aquí. Everything Eco nació de mi vida.',
      p2: 'Mi papá veía valor donde otros veían basura. Recogía muebles y electrónicos que la gente botaba, los reparaba, y los enviaba a familias en la República Dominicana que no podían pagarlos. De niño no siempre lo entendía. Ahora lo sé — él no recogía basura. Él veía el valor que otros no podían ver.',
      p3a: 'Mi mamá me enseñó a crear mis propias oportunidades en vez de esperarlas. Y los dos me enseñaron la lección detrás de todo: ',
      p3strong: 'trata a la gente con dignidad.',
      p3b: ' Cuando los vecinos venían a vender lo poquito que tenían, mis padres compraban lo que podían y los trataban como seres humanos.',
      p4: 'En el 2018 perdí a mi madre. Eso me enseñó que el tiempo importa — que quería construir algo con significado mientras tengo la oportunidad. Everything Eco es mi respuesta. 💚',
      pro: 'Y esto no es solo corazón — es oficio. Un programa laboral de St. Nicks Alliance me llevó al mundo del reciclaje de electrónicos en Brooklyn, donde probé y reparé miles de equipos, coordiné recogidas por toda la ciudad, manejé inventario y entrené a nuevos compañeros. Seguí formándome en IT con Per Scholas, y hoy manejo un negocio de reventa de electrónicos. Everything Eco está construido sobre esa experiencia.',
      line: 'El reciclaje es nuestra base. Las personas son nuestro propósito.',
      sign: '— Julio Ramirez, Fundador de Everything Eco',
      coTitle: 'Y conoce a Nick 🤝',
      coName: 'Nickel “Nick” John', coRole: 'Cofundador',
      coQuote: '“Queremos reciclar materiales y también reciclar conocimiento, habilidades y oportunidad.”',
      coP1: 'Nick nació y se crió en Guyana, viendo a sus padres trabajar incansablemente por la oportunidad de traerlo a los Estados Unidos. Cuando llegó, vio esa lucha en todas partes — inmigrantes y gente nacida aquí por igual, haciendo todo lo posible por salir adelante y apoyar a los suyos.',
      coP2: 'Desde niño desarma electrónicos. Entre su trabajo con iFixit, ayudar a manejar operaciones de recuperación de electrónicos en Nueva York y su formación en ingeniería de software en la Asurion Academy, se convirtió en mi mentor en esta industria — la persona que me enseñó cómo funcionan de verdad los sistemas profesionales de prueba, reparación y reciclaje.',
      coP3: 'A lo largo de su vida, Nick conoció amigos, familiares y compañeros que estaban listos para trabajar, aprender y cambiar sus vidas — pero a quienes rara vez les daban una oportunidad real de demostrarlo. Esa es una de las razones principales por las que ayudó a fundar Everything Eco: darle a la gente habilidades, conocimiento y oportunidades que puedan llevar consigo toda la vida.',
      coMore: 'Lee la historia de Nick, en sus propias palabras ↓',
      coLess: 'Cerrar la historia de Nick ↑',
      coSign: '— Nick John, Cofundador de Everything Eco',
      coFull: [
        'Nací y me crié en Guyana, Sudamérica. Crecí viendo a mis padres trabajar incansablemente por la oportunidad de traerme a los Estados Unidos y construir una vida mejor para nuestra familia.',
        'Cuando llegué a este país, vi esa lucha en todas partes. Vi a inmigrantes trabajando sin descanso para crear un futuro para sus familias, pero también vi a personas nacidas aquí enfrentando muchos de los mismos obstáculos. Hacían todo lo posible por sobrevivir, salir adelante, apoyar a la gente que aman y aportar al mundo que los rodea.',
        'Esas experiencias me enseñaron lo profundamente conectados que estamos todos.',
        'A lo largo de mi vida he conocido amigos, familiares, compañeros de trabajo y miembros de mi comunidad que buscaban una segunda oportunidad. Muchos estaban listos para trabajar, aprender y cambiar sus vidas, pero rara vez se les daba una oportunidad real de demostrarlo.',
        'Esa es una de las razones principales por las que ayudé a fundar Everything Eco.',
        'A través de esta compañía, quiero darle a la gente habilidades prácticas, conocimiento, guía y oportunidades que puedan llevar consigo por el resto de sus vidas. Alguien puede usar esas habilidades para crecer dentro de Everything Eco, calificar para otra carrera, mantener a su familia o algún día fundar su propia empresa.',
        'Vi a mis padres trabajar duro por años sin que siempre les dieran una oportunidad justa de avanzar. Vi lo que es trabajar sin saber si algo mejor llegaría algún día. No quiero que ese sea el futuro de la próxima generación.',
        'Al mismo tiempo, nuestro planeta está quemando recursos valiosos. Cada día botamos electrónicos, plástico, papel, ropa, equipos, conocimiento, talento y potencial humano — muchas veces antes de reconocer su verdadero valor.',
        'Everything Eco está construido sobre la convicción de que nada de eso debería desperdiciarse.',
        'Queremos reciclar materiales y también reciclar conocimiento, habilidades y oportunidad. Al darle una segunda vida a los recursos y una segunda oportunidad a las personas, fortalecemos nuestras comunidades, creamos nuevos caminos y protegemos el planeta al mismo tiempo.',
        'Porque cuando la gente recibe las herramientas y la oportunidad de crecer, todos ganamos.',
      ],
      together: 'Dos neoyorquinos. Una convicción: nada — y nadie — debería ser tratado como desecho.',
    },
    programs: {
      label: 'Comunidad y Programas', title1: 'Más que reciclaje —', title2: 'es un movimiento. 🗽',
      sub: 'Everything Eco se construye cuadra por cuadra. Damos trabajo a los jóvenes de NYC, enverdecemos nuestros barrios y enseñamos a la próxima generación a cuidar la ciudad que llaman hogar.',
      cards: [
        { h: 'Cuadrillas Juveniles de Limpieza', p: 'Contratamos y entrenamos a jóvenes del barrio para limpiezas y recogidas — trabajo pagado que construye habilidades, orgullo y una ciudad más limpia.' },
        { h: 'Jardines Comunitarios', p: 'Ayudamos a convertir lotes vacíos en espacios verdes, apoyando jardines por todo Brooklyn y más allá.' },
        { h: 'Educación de Reciclaje', p: 'Talleres prácticos en escuelas y centros comunitarios — enseñando cómo (y por qué) reciclar bien.' },
        { h: 'Jornadas del Barrio', p: 'Jornadas de e-waste, ropa y comida que unen a toda la cuadra por una causa común.' },
      ],
    },
    heroes: {
      kicker: '★ El Universo Everything Eco ★', title: 'Únete a los Eco-Aliados',
      sub: 'Trash-Bot está creciendo — hecho de todo lo que nuestra ciudad bota. Cada recogida que agendas es un golpe contra él. Ponte el traje, Nueva York. 🦸‍♀️🦸',
      trailerTag: '🎬 AVANCE OFICIAL',
      trailerCap: 'Earth Jr. y la Eco-Force — toca para escuchar 🔊',
      roster: [
        'El héroe. Valiente, esperanzado, nacido en Brooklyn.',
        'Recolecta y separa todo lo que se puede reusar.',
        'Inteligente y cool. Mantiene el agua limpia.',
        'Aprovecha el sol para dar energía al equipo.',
        'Rápido y fresco, mantiene el aire en movimiento.',
        'Fuerte y sabio. Protege todo lo que vive.',
      ],
      cap1: 'Número #1 — “Trash-Bot Rises”', cap2: 'Eco-Héroes, en las calles de NYC',
      shopTitle: '🛍️ La Tienda Eco-Hero', shopSub1: 'Cómics, figuras de vinilo y coleccionables — ', shopSub2: '¡muy pronto!',
      soon: 'Muy pronto', kidsFree: '¡Niños: 1 GRATIS! 🎁', notify: '🔔 Avísame',
      shop: [
        { h: 'Earth Jr. — Planet Hero', p: 'Figura coleccionable de vinilo Serie 1, con capa y emblema de reciclaje que brilla.' },
        { h: 'Mini-Figuras Eco-Aliados', p: '¡Colecciónalos los 5! Binny, Drip, Windy, Sunny y Sprout. Los niños reciben una gratis en cada recogida.' },
        { h: '“Trash-Bot Rises” — Número #1', p: 'El primer cómic de Everything Eco. A todo color, edición de colección.' },
      ],
      banner1: 'Mini-figura gratis para los niños', banner2: ' en cada recogida — ¡hay que coleccionarlas todas!',
      btnPlay: '🎮 Juega "Trash-Bot Rises"', btnHero: 'Conviértete en Eco-Héroe →',
      btnVerse: 'Entra al EcoVerse', btnShop: '🛍️ Visita la Tienda',
    },
    doors: {
      title: '¿Qué te trae por aquí hoy?',
      cards: [
        { h: 'Mi Hogar', p: 'Recogidas gratis de electrónicos, ropa y muebles — directo en tu puerta.', go: 'Agenda una recogida' },
        { h: 'Mi Negocio', p: 'Vaciados, recuperación de e-waste y documentación para oficinas, edificios y escuelas.', go: 'Pide tu cotización gratis' },
        { h: 'Niños y Escuelas', p: 'Earth Jr., la Eco-Force, juegos, cómics y la Eco Academia.', go: 'Entra al EcoVerse' },
        { h: 'Necesito una Mano', p: 'Trabajos, entrenamientos, computadoras, comida y recursos reales de NYC — sin pena, solo próximos pasos.', go: 'Visita el Centro de Oportunidades' },
      ],
    },
    partners: {
      label: 'Alíate con Nosotros', title: 'Construyamos juntos una NYC más verde.',
      sub: 'Nos unimos con negocios, escuelas, refugios y organizaciones de toda la ciudad para mantener las cosas buenas fuera del vertedero y en manos de quien las necesita.',
      cards: [
        { h: 'Negocios y Restaurantes', p: 'Agenda recogidas en volumen de equipos viejos, electrónicos y excedentes — con recibo deducible de impuestos.' },
        { h: 'Escuelas y Universidades', p: 'Organiza una jornada de e-waste o ropa. Nosotros manejamos la logística y compartimos el impacto con tu comunidad.' },
        { h: 'Refugios y Organizaciones', p: 'Recibe electrónicos reparados, ropa limpia y esenciales para la gente que sirves.' },
        { h: 'Administradores de Edificios', p: 'Ofrece recogidas de reciclaje fáciles y recurrentes como beneficio para tus residentes e inquilinos.' },
      ],
      ctaP: '¿Te interesa aliarte con Everything Eco?', ctaBtn: 'Hazte Aliado →',
    },
    faq: {
      label: '¿Preguntas? Respondidas.', title: 'Preguntas frecuentes',
      items: [
        { q: '¿De verdad es gratis?', a: '¡Sí! Las recogidas de e-waste, ropa y donaciones del hogar son 100% gratis para los neoyorquinos. Para vaciados grandes de negocios u oficinas, primero te damos una cotización gratis y sin compromiso — sin cargos ocultos, jamás.' },
        { q: '¿Qué pueden recoger?', a: 'Electrónicos y e-waste (laptops, teléfonos, TVs, cables), ropa y textiles, muebles, artículos del hogar y donaciones de comida. Para negocios también hacemos recogidas en volumen y vaciados completos de oficinas.' },
        { q: '¿Y mis datos — están seguros?', a: 'Totalmente. En computadoras y teléfonos podemos borrar o destruir físicamente los discos duros para que tus datos personales o de tu empresa desaparezcan para siempre. Solo avísanos cuando agendes tu recogida.' },
        { q: '¿Qué pasa con mis cosas después de la recogida?', a: 'Lo que funciona se limpia, se repara y se dona a personas y familias que lo necesitan. Todo lo demás se recicla responsablemente. La meta es simple: nada usable termina en el vertedero.' },
        { q: '¿Dónde dan servicio?', a: 'En los cinco condados de Nueva York — Brooklyn, Manhattan, Queens, el Bronx y Staten Island.' },
        { q: '¿Me dan recibo por mi donación?', a: 'Sí — las donaciones que califican pueden traer un recibo deducible de impuestos a través de nuestros socios sin fines de lucro. Solo pídelo al agendar y nosotros nos encargamos del papeleo.' },
      ],
      ctaP: '¿Todavía tienes una pregunta?', ctaBtn: 'Escríbenos →',
    },
    news: {
      title: 'Mantente al tanto — y con el planeta. 🌿',
      sub: 'Recibe actualizaciones mensuales de impacto, tips de reciclaje, descuentos de aliados y acceso anticipado a lo nuevo.',
      name: 'Tu nombre', email: 'Tu correo electrónico', btn: 'Suscríbete — Es Gratis 🎉',
      fine: 'Nada de spam. Cancela cuando quieras. Respetamos tu correo.',
      alert: '¡Ya estás dentro! Bienvenido a la comunidad de Everything Eco 💚',
    },
  },
};

const HomePage: React.FC = () => {
  const heroRef = useRef<HTMLElement>(null);
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const [trailerMuted, setTrailerMuted] = useState(true);
  const [nickOpen, setNickOpen] = useState(false);
  const trailerRef = useRef<HTMLVideoElement>(null);
  const { lang, t } = useLang();

  // Pause the teaser (and its sound) whenever it scrolls out of view; resume when back.
  useEffect(() => {
    const v = trailerRef.current;
    if (!v) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) v.play().catch(() => {});
          else v.pause();
        });
      },
      { threshold: 0.35 }
    );
    io.observe(v);
    return () => io.disconnect();
  }, []);
  const L = HOME_COPY[lang];
  useReveal();

  // Hero scroll parallax — writes --sp, consumed in HomePage.css
  useEffect(() => {
    const hero = heroRef.current;
    if (!hero) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const onScroll = () => {
      const sp = Math.min(window.scrollY / window.innerHeight, 1);
      hero.style.setProperty('--sp', sp.toFixed(3));
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  return (
    <div className="ee-home">
      <ChapterNav />
      {/* ── Hero ── */}
      <section className="ee-hero" ref={heroRef}>
        <div className="ee-hero-deco" aria-hidden="true">
          <span className="ee-float f1">🌎</span>
          <span className="ee-float f2">♻️</span>
          <span className="ee-float f3">⚡</span>
          <span className="ee-float f4">🌳</span>
          <span className="ee-float f5">💥</span>
          <span className="ee-float f6">💧</span>
        </div>
        <div className="ee-hero-content">
          <div className="ee-hero-badge">🗽 {t('hero.badge')}</div>
          <h1>
            {lang === 'es' ? (
              <>
                Cada <span className="ee-accent-green">persona</span>. Cada <span className="ee-accent-blue">cosa</span>.<br />
                Cada <span className="ee-accent-orange">comunidad</span>.<br />
                Todo tiene <em className="ee-hero-value">valor</em>.
              </>
            ) : (
              <>
                Every <span className="ee-accent-green">person</span>. Every <span className="ee-accent-blue">item</span>.<br />
                Every <span className="ee-accent-orange">community</span>.<br />
                Everything has <em className="ee-hero-value">value</em>.
              </>
            )}
          </h1>
          <p className="ee-hero-sub">{t('hero.sub')}</p>
          <div className="ee-hero-value-chips">
            <span>💸 {t('hero.chip1')}</span>
            <span>🗽 {t('hero.chip2')}</span>
            <span>⏱️ {t('hero.chip3')}</span>
          </div>
          <div className="ee-hero-btns">
            <Link to="/pickup" className="ee-btn ee-btn-green" onClick={() => track('pickup_cta_clicked', { source: 'hero' })}>{BOOKINGS_OPEN ? t('hero.btnPickup') : t('hero.btnWaitlist')}</Link>
            <Link to="/experience" className="ee-btn ee-btn-ghost">{t('hero.btnExperience')}</Link>
          </div>
          <Link to="/game" className="ee-hero-eco-force">{t('hero.ecoforce')}</Link>
        </div>
        <div className="ee-hero-showcase">
          <img
            className="ee-hero-photo"
            src={`${process.env.PUBLIC_URL}/community-hero.png`}
            alt="Everything Eco volunteers cleaning up a Brooklyn block, sorting donations, and giving away free clothes"
          />
          <div className="ee-hero-chip">
            <span className="ee-hero-chip-dot" />
            Real New Yorkers, real impact
          </div>
          <Link to="/game" className="ee-hero-comic" aria-label="Read the Eco-Force comic and play the game">
            <img src={`${process.env.PUBLIC_URL}/battle-cover.png`} alt="Earth Jr. and the Eco-Force comic cover" />
            <span className="ee-hero-comic-tag">★ ISSUE #1</span>
          </Link>
          <img
            className="ee-hero-ej-fly"
            src={`${process.env.PUBLIC_URL}/earthjr-fly.png`}
            alt="Earth Jr. flying"
          />
        </div>
      </section>

      {/* ── Doors: pick your path ── */}
      <section className="ee-doors" aria-label={L.doors.title}>
        <div className="ee-container">
          <div className="ee-doors-title">{L.doors.title}</div>
          <div className="ee-doors-grid">
            {L.doors.cards.map((d, i) => (
              <Link
                to={['/pickup', '/business-quote', '/ecoverse', '/help'][i]}
                className="ee-door"
                key={i}
                onClick={() => track('audience_selected', { audience: ['residents', 'business', 'kids', 'help'][i], source: 'doors' })}
              >
                <span className="ee-door-icon">{['🏠', '💼', '🚀', '🧭'][i]}</span>
                <strong>{d.h}</strong>
                <p>{d.p}</p>
                <span className="ee-door-go">{d.go} →</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── Marquee band ── */}
      <div className="ee-marquee" aria-hidden="true">
        <div className="ee-marquee-track">
          {[0, 1].map((k) => (
            <span key={k}>{L.marquee}&nbsp;</span>
          ))}
        </div>
      </div>

      {/* ── How It Works ── */}
      <section id="how" className="ee-section ee-how">
        <div className="ee-container">
          <div className="ee-section-header">
            <div className="ee-section-label">{L.how.label}</div>
            <h2 className="ee-section-title">{L.how.title}</h2>
            <p className="ee-section-sub">{L.how.sub}</p>
          </div>
          <div className="ee-steps">
            {L.how.steps.map((s, i) => (
              <div className="ee-step-card" key={i}>
                <div className="ee-step-num">{s.num}</div>
                <div className={`ee-step-icon ${['green', 'blue', 'orange'][i]}`}>{['📅', '🚐', '🌱'][i]}</div>
                <h3>{s.h}</h3>
                <p>{s.p}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Who We Serve (interactive audience switcher — data in src/data/audiences.ts) ── */}
      <AudienceTabs />

      {/* ── We take everything (cinematic material tiles — data in src/data/materials.ts) ── */}
      <WeTakeStrip />

      {/* ── Impact Calculator (nav "Impact" lands here; full accepted-items list lives on /pickup) ── */}
      <ImpactCalculator />

      {/* ── For Businesses ── */}
      <section id="business" className="ee-section ee-biz">
        <div className="ee-container">
          <div className="ee-section-header" style={{ textAlign: 'center', maxWidth: 700, margin: '0 auto 56px' }}>
            <div className="ee-section-label" style={{ justifyContent: 'center' }}>{L.biz.label}</div>
            <h2 className="ee-section-title">{L.biz.title}</h2>
            <p className="ee-section-sub" style={{ margin: '0 auto' }}>{L.biz.sub}</p>
          </div>
          <div className="ee-biz-cards">
            {L.biz.cards.map((c, i) => (
              <div className={`ee-biz-card${i === 0 ? ' feature' : ''}`} key={i}>
                {i === 0 && <div className="ee-biz-badge-sm">{L.biz.badge}</div>}
                <div className={`ee-biz-icon ${['blue', 'green', 'orange', 'green', 'orange'][i]}`}>{['🔒', '🖥️', '🏢', '📊', '🧾'][i]}</div>
                <h3>{c.h}</h3>
                <p>{c.p}</p>
              </div>
            ))}
          </div>
          <div className="ee-biz-trust">
            {L.biz.trust.map((tr) => <span key={tr}>{tr}</span>)}
          </div>
          <div className="ee-biz-cta">
            <Link to="/business-quote" className="ee-btn ee-btn-blue">{L.biz.cta}</Link>
          </div>
        </div>
      </section>

      {/* ── Why Choose Us ── */}
      <section id="why" className="ee-section ee-why">
        <div className="ee-container">
          <div className="ee-section-header" style={{ textAlign: 'center', maxWidth: 640, margin: '0 auto 56px' }}>
            <div className="ee-section-label" style={{ justifyContent: 'center' }}>{L.why.label}</div>
            <h2 className="ee-section-title">{L.why.title}</h2>
            <p className="ee-section-sub" style={{ margin: '0 auto' }}>{L.why.sub}</p>
          </div>
          <div className="ee-compare">
            <div className="ee-compare-row ee-compare-head">
              <div className="ee-compare-feat"></div>
              <div className="ee-compare-us">{L.why.us}</div>
              <div className="ee-compare-them">{L.why.them}</div>
            </div>
            {L.why.rows.map(([feat, them], i) => (
              <div className="ee-compare-row" key={i}>
                <div className="ee-compare-feat">{feat}</div>
                <div className="ee-compare-us"><span className="ee-check">✓</span></div>
                <div className="ee-compare-them"><span className="ee-cross">✕</span> <span className="ee-them-note">{them}</span></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Rewards ── */}
      <section id="rewards" className="ee-section ee-rewards">
        <div className="ee-container">
          <div className="ee-section-header">
            <div className="ee-section-label">{L.rewards.label}</div>
            <h2 className="ee-section-title">{L.rewards.title}</h2>
            <p className="ee-section-sub">{L.rewards.sub}</p>
          </div>
          <div className="ee-reward-cards">
            {L.rewards.cards.map((c, i) => (
              <div className={`ee-reward-card${i === 1 ? ' featured' : ''}`} key={i}>
                {i === 1 && <div className="ee-reward-badge">{L.rewards.popular}</div>}
                <div className="ee-reward-icon">{['☕', '🛍️', '🌱', '🎟️'][i]}</div>
                <div className="ee-reward-pts">{c.pts}</div>
                <div className="ee-reward-name">{c.name}</div>
                <div className="ee-reward-desc">{c.desc}</div>
              </div>
            ))}
          </div>
          <div className="ee-rewards-cta">
            <p>{L.rewards.ctaP}</p>
            <Link to="/pickup" className="ee-btn ee-btn-green">{L.rewards.ctaBtn}</Link>
          </div>
        </div>
      </section>

      {/* ── The Item Journey (cinematic sticky chapter; static cards on mobile/reduced-motion) ── */}
      <ItemJourney />

      {/* ── Founder ── */}
      <section id="founder" className="ee-section ee-founder">
        <div className="ee-container">
          <div className="ee-founder-grid">
            <div className="ee-founder-media">
              <div className="ee-founder-photo has-photo">
                <img
                  src={`${process.env.PUBLIC_URL}/founder.jpg`}
                  alt="Julio, founder of Everything Eco, in the workshop surrounded by rescued laptops"
                  className="ee-founder-img"
                />
              </div>
              <div className="ee-founder-badge">{L.founder.badge}</div>
            </div>
            <div className="ee-founder-content">
              <div className="ee-section-label">{L.founder.label}</div>
              <h2 className="ee-section-title">{L.founder.title}</h2>
              <p className="ee-founder-quote">{L.founder.quote}</p>
              <p>{L.founder.p1}</p>
              <p>{L.founder.p2}</p>
              <p>
                {L.founder.p3a}<strong>{L.founder.p3strong}</strong>{L.founder.p3b}
              </p>
              <p>{L.founder.p4}</p>
              <p>{L.founder.pro}</p>
              <p className="ee-founder-line">{L.founder.line}</p>
              <div className="ee-founder-sign">{L.founder.sign}</div>
              <div className="ee-founder-tagline">Trash to Tech · Waste to Worth · Pain to Purpose</div>
            </div>
          </div>

          <div className="ee-cofounder">
            <div className="ee-cofounder-media">
              <img
                src={`${process.env.PUBLIC_URL}/nick.jpg`}
                alt="Nick John, co-founder of Everything Eco"
                className="ee-cofounder-img"
              />
            </div>
            <div className="ee-cofounder-body">
              <h3>{L.founder.coTitle}</h3>
              <div className="ee-cofounder-name">{L.founder.coName} · <span>{L.founder.coRole}</span></div>
              <p className="ee-founder-quote">{L.founder.coQuote}</p>
              <p>{L.founder.coP1}</p>
              <p>{L.founder.coP2}</p>
              <p className="ee-cofounder-belief">{L.founder.coP3}</p>
              <button
                type="button"
                className="ee-cofounder-more"
                onClick={() => setNickOpen((o) => !o)}
                aria-expanded={nickOpen}
              >
                {nickOpen ? L.founder.coLess : L.founder.coMore}
              </button>
              {nickOpen && (
                <div className="ee-cofounder-full">
                  {L.founder.coFull.map((p, i) => (
                    <p key={i}>{p}</p>
                  ))}
                  <div className="ee-founder-sign">{L.founder.coSign}</div>
                </div>
              )}
            </div>
          </div>
          <p className="ee-founders-together">{L.founder.together}</p>
        </div>
      </section>

      {/* ── Community & Programs ── */}
      <section id="programs" className="ee-section ee-programs">
        <div className="ee-container">
          <div className="ee-section-header">
            <div className="ee-section-label">{L.programs.label}</div>
            <h2 className="ee-section-title">{L.programs.title1}<br />{L.programs.title2}</h2>
            <p className="ee-section-sub">{L.programs.sub}</p>
          </div>
          <div className="ee-program-cards">
            {L.programs.cards.map((c, i) => (
              <div className="ee-program-card" key={i}>
                <div className={`ee-program-icon ${['green', 'blue', 'orange', 'green'][i]}`}>{['🧹', '🌻', '🎓', '📦'][i]}</div>
                <h3>{c.h}</h3>
                <p>{c.p}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── EcoVerse band (kids & families gateway — the full universe lives at /ecoverse) ── */}
      <section id="ecoheroes" className="ee-section ee-heroes ee-heroes-band">
        <div className="ee-container ee-heroes-band-grid">
          <div className="ee-trailer ee-trailer-compact">
            <div
              className="ee-trailer-frame"
              onClick={() => setTrailerMuted((m) => !m)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setTrailerMuted((m) => !m); } }}
              role="button"
              aria-label="Toggle teaser sound"
              tabIndex={0}
            >
              <video
                ref={trailerRef}
                src={`${process.env.PUBLIC_URL}/eco-force-teaser.mp4`}
                poster={`${process.env.PUBLIC_URL}/eco-force-teaser-poster.jpg`}
                autoPlay
                muted={trailerMuted}
                loop
                playsInline
              />
              <span className="ee-trailer-tag">{L.heroes.trailerTag}</span>
              <span className="ee-trailer-sound">{trailerMuted ? '🔇' : '🔊'}</span>
            </div>
          </div>
          <div className="ee-heroes-band-content">
            <div className="ee-heroes-kicker">{L.heroes.kicker}</div>
            <h2 className="ee-heroes-title">{L.heroes.title}</h2>
            <p className="ee-heroes-sub">{L.heroes.sub}</p>
            <div className="ee-heroes-btns">
              <Link to="/ecoverse" className="ee-btn ee-btn-orange">🌌 {L.heroes.btnVerse}</Link>
              <Link to="/game" className="ee-btn ee-btn-ghost ee-btn-light">{L.heroes.btnPlay}</Link>
              <Link to="/ecoverse#shop" className="ee-btn ee-btn-ghost ee-btn-light">{L.heroes.btnShop}</Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── Partner With Us ── */}
      <section id="partners" className="ee-section ee-partners">
        <div className="ee-container">
          <div className="ee-section-header" style={{ textAlign: 'center', margin: '0 auto 60px', maxWidth: 640 }}>
            <div className="ee-section-label" style={{ textAlign: 'center' }}>{L.partners.label}</div>
            <h2 className="ee-section-title">{L.partners.title}</h2>
            <p className="ee-section-sub" style={{ margin: '0 auto' }}>{L.partners.sub}</p>
          </div>
          <div className="ee-partner-cards">
            {L.partners.cards.map((c, i) => (
              <div className="ee-partner-card" key={i}>
                <div className={`ee-partner-icon ${['green', 'blue', 'orange', 'green'][i]}`}>{['🏢', '🎓', '🤝', '🏬'][i]}</div>
                <h3>{c.h}</h3>
                <p>{c.p}</p>
              </div>
            ))}
          </div>
          <div className="ee-partners-cta">
            <p>{L.partners.ctaP}</p>
            <Link to="/contact" className="ee-btn ee-btn-blue">{L.partners.ctaBtn}</Link>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section id="faq" className="ee-section ee-faq">
        <div className="ee-container">
          <div className="ee-section-header" style={{ textAlign: 'center', maxWidth: 620, margin: '0 auto 48px' }}>
            <div className="ee-section-label" style={{ justifyContent: 'center' }}>{L.faq.label}</div>
            <h2 className="ee-section-title">{L.faq.title}</h2>
          </div>
          <div className="ee-faq-list">
            {L.faq.items.map((item, i) => (
              <div className={`ee-faq-item${openFaq === i ? ' open' : ''}`} key={i}>
                <button className="ee-faq-q" onClick={() => { if (openFaq !== i) track('faq_opened', { index: i }); setOpenFaq(openFaq === i ? null : i); }} aria-expanded={openFaq === i}>
                  <span>{item.q}</span>
                  <span className="ee-faq-toggle">{openFaq === i ? '–' : '+'}</span>
                </button>
                <div className="ee-faq-a"><p>{item.a}</p></div>
              </div>
            ))}
          </div>
          <div className="ee-faq-cta">
            <p>{L.faq.ctaP}</p>
            <Link to="/contact" className="ee-btn ee-btn-green">{L.faq.ctaBtn}</Link>
          </div>
        </div>
      </section>

      {/* ── Newsletter ── */}
      <section id="newsletter" className="ee-section ee-newsletter">
        <div className="ee-container">
          <div className="ee-newsletter-card">
            <div>
              <h2>{L.news.title}</h2>
              <p>{L.news.sub}</p>
            </div>
            <form
              className="ee-newsletter-form"
              name="newsletter"
              method="POST"
              data-netlify="true"
              data-netlify-honeypot="bot-field"
              onSubmit={(e) => {
                e.preventDefault();
                const form = e.currentTarget;
                const body = new URLSearchParams(new FormData(form) as any).toString();
                track('newsletter_submitted');
                fetch('/', { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body })
                  .catch(() => {})
                  .finally(() => { alert(L.news.alert); form.reset(); });
              }}
            >
              <input type="hidden" name="form-name" value="newsletter" />
              <p style={{ position: 'absolute', left: '-9999px' }} aria-hidden="true">
                <label>Don't fill this out: <input name="bot-field" tabIndex={-1} autoComplete="off" /></label>
              </p>
              <input type="text" name="name" placeholder={L.news.name} />
              <input type="email" name="email" placeholder={L.news.email} required />
              <button type="submit" className="ee-btn ee-btn-orange">{L.news.btn}</button>
              <p className="ee-newsletter-fine">{L.news.fine}</p>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
